import React, { useEffect, useState } from "react";
import { OverlayTrigger, Tooltip, Offcanvas } from "react-bootstrap";
import ImageWithBasePath from "../../../../core/img/imagewithbasebath";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { RotateCcw } from "feather-icons-react/build/IconComponents";

import Table from "../../../../core/pagination/datatable";
import { Select, Spin } from "antd";
import { ROLL_CALL_API_BASE_URL } from "../RollCallConfig/RollCallConfig";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faComment,
} from "@fortawesome/free-solid-svg-icons";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

const LeaveAccurals = () => {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [tokenData, setTokenData] = useState(null);
  const [superIdData, setSuperIdData] = useState(null);

  // Offcanvas States
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [leaveDetails, setLeaveDetails] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const [perm, setPerm] = useState({
    Read: false,
    Write: false,
    Delete: false,
    Export: false,
  });

  const years = [currentYear - 1, currentYear, currentYear + 1];

  useEffect(() => {
    const stored = sessionStorage.getItem("rollcall-permissions");
    if (!stored) return;
    const map = JSON.parse(stored);
    const path = location.pathname.slice(1);
    const perms = map[path];
    if (!perms) {
      navigate("/rollcall/dashboard");
      return;
    }
    setPerm({
      Read: perms.canRead,
      Write: perms.canWrite,
      Delete: perms.canDelete,
      Export: perms.canExport,
    });
  }, [location, navigate]);

  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    const superId = sessionStorage.getItem("superId");

    if (token && superId) {
      setTokenData(token);
      setSuperIdData(superId);
    } else {
      navigate("/signin");
    }
  }, [navigate]);

  const handleLeavesView = async () => {
    if (!superIdData || !tokenData || !selectedYear) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${ROLL_CALL_API_BASE_URL}portal/leave/getLeaveAccrualsForAdmin?SuperId=10051&Year=2020`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tokenData}`,
          },
        },
      );
      const result = await response.json();

      if (result?.status) {
        setData(result.ResultData || []);
      } else {
        console.error("Failed to fetch data:", result?.ErrorMsg);
        setData([]);
      }
    } catch (error) {
      console.error("Error fetching leave accruals:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaveDetails = async (regId) => {
    if (!regId || !selectedYear || !superIdData || !tokenData) return;

    setDetailLoading(true);
    try {
      const response = await fetch(
        `${ROLL_CALL_API_BASE_URL}portal/leave/getempleaves?regid=${regId}&year=2020&superid=10051`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tokenData}`,
          },
        },
      );
      const result = await response.json();

      if (result?.status) {
        setLeaveDetails(result.ResultData || []);
      } else {
        setLeaveDetails([]);
      }
    } catch (error) {
      console.error("Error fetching leave details:", error);
      setLeaveDetails([]);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleTakenClick = (record) => {
    setSelectedEmployee(record);
    setShowOffcanvas(true);
    fetchLeaveDetails(record.RegId);
  };

  const columns = [
    {
      title: "Employee",
      dataIndex: "UserName",
      sorter: (a, b) => (a.UserName || "").localeCompare(b.UserName || ""),
      render: (text) => (
        <span>{text?.length > 22 ? `${text.substring(0, 22)}...` : text}</span>
      ),
    },
    { title: "Badge", dataIndex: "CardId" },
    {
      title: "Designation",
      dataIndex: "Designation",
      sorter: (a, b) =>
        (a.Designation || "").localeCompare(b.Designation || ""),
      render: (text) => (
        <span>{text?.length > 20 ? `${text.substring(0, 20)}...` : text}</span>
      ),
    },
    {
      title: "Carry Forward",
      dataIndex: "CarryForwardLeaves",
      sorter: (a, b) =>
        (a.CarryForwardLeaves || 0) - (b.CarryForwardLeaves || 0),
      align: "center",
    },
    {
      title: "Earned",
      dataIndex: "Accrued",
      sorter: (a, b) => (a.Accrued || 0) - (b.Accrued || 0),
      align: "center",
    },
    {
      title: "Taken",
      dataIndex: "Taken",
      sorter: (a, b) => (a.Taken || 0) - (b.Taken || 0),
      align: "center",
      render: (text, record) => (
        <span
          className="text-primary fw-bold"
          style={{ cursor: "pointer", textDecoration: "underline" }}
          onClick={() => handleTakenClick(record)}
        >
          {text || 0}
        </span>
      ),
    },
    {
      title: "Balance",
      dataIndex: "Balance",
      sorter: (a, b) => (a.Balance || 0) - (b.Balance || 0),
      align: "center",
      render: (text) => (
        <span className={text <= 2 ? "text-danger fw-bold" : ""}>
          {text || 0}
        </span>
      ),
    },
  ];

  const generatePDF = () => {
    if (!data.length) return;
    const doc = new jsPDF();
    doc.text(`Leave Accruals - ${selectedYear}`, 14, 10);

    const tableColumn = [
      "Employee",
      "Badge",
      "Designation",
      "Carry Forward",
      "Accrued",
      "Taken",
      "Balance",
    ];
    const tableRows = data.map((item) => [
      item.UserName || "N/A",
      item.CardId || "N/A",
      item.Designation || "N/A",
      item.CarryForwardLeaves || 0,
      item.Accrued || 0,
      item.Taken || 0,
      item.Balance || 0,
    ]);

    doc.autoTable({ head: [tableColumn], body: tableRows, startY: 20 });
    doc.save(`Leave_Accruals_${selectedYear}.pdf`);
  };

  const handleDownloadExcel = () => {
    if (!data.length) return;
    const wb = XLSX.utils.book_new();
    const wsData = data.map((item, index) => ({
      "S.No": index + 1,
      Employee: item.UserName,
      Badge: item.CardId,
      Designation: item.Designation,
      "Carry Forward": item.CarryForwardLeaves,
      Accrued: item.Accrued,
      Taken: item.Taken,
      Balance: item.Balance,
    }));

    const ws = XLSX.utils.json_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "Leave Accruals");
    XLSX.writeFile(wb, `Leave_Accruals_${selectedYear}.xlsx`);
  };

  const handleReload = () => window.location.reload();

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/rollcall/dashboard">
                      <i className="fas fa-home fa-lg" />
                    </Link>
                  </li>
                  <h4 className="breadcrumb-item" aria-current="page">
                    Leave Accruals
                  </h4>
                </ol>
              </nav>
              <h6>View employee leave accruals by year</h6>
            </div>
          </div>

          <ul className="table-top-head">
            {perm?.Export && (
              <>
                <li>
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>Pdf</Tooltip>}
                  >
                    <Link onClick={generatePDF}>
                      <ImageWithBasePath
                        src="assets/img/icons/pdf.svg"
                        alt="pdf"
                      />
                    </Link>
                  </OverlayTrigger>
                </li>
                <li>
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>Excel</Tooltip>}
                  >
                    <Link onClick={handleDownloadExcel}>
                      <ImageWithBasePath
                        src="assets/img/icons/excel.svg"
                        alt="excel"
                      />
                    </Link>
                  </OverlayTrigger>
                </li>
              </>
            )}
            <li onClick={handleReload}>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Refresh</Tooltip>}
              >
                <Link>
                  <RotateCcw />
                </Link>
              </OverlayTrigger>
            </li>
          </ul>
        </div>

        {perm.Read ? (
          <div className="card table-list-card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div className="search-set"></div>
              <div className="d-flex align-items-center gap-3">
                <Select
                  placeholder="Select Year"
                  style={{ width: 120 }}
                  value={selectedYear}
                  onChange={(value) => setSelectedYear(value)}
                >
                  {years.map((year) => (
                    <Select.Option key={year} value={year}>
                      {year}
                    </Select.Option>
                  ))}
                </Select>

                <button className="btn btn-primary" onClick={handleLeavesView}>
                  <FontAwesomeIcon icon={faMagnifyingGlass} />
                </button>
              </div>
            </div>

            <div className="card-body">
              {loading ? (
                <div className="text-center my-5">
                  <Spin size="large" />
                  <p>Loading leave accruals...</p>
                </div>
              ) : (
                <Table columns={columns} dataSource={data} pgno={true} />
              )}
            </div>
          </div>
        ) : (
          <div className="card">
            <div className="card-body text-center">
              <h1>You dont have access to this page</h1>
            </div>
          </div>
        )}
      </div>

      <Offcanvas
        show={showOffcanvas}
        onHide={() => setShowOffcanvas(false)}
        placement="end"
        backdrop="static"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            Leaves Taken - {selectedEmployee?.UserName}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {detailLoading ? (
            <div className="text-center my-5">
              <Spin size="large" />
            </div>
          ) : leaveDetails.length === 0 ? (
            <p className="text-center text-muted">
              No leaves taken by this employee.
            </p>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered">
                <thead className="table-light">
                  <tr>
                    <th>Date</th>
                    <th>Leave Type</th>
                    <th>Days</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {leaveDetails.map((item) => (
                    <tr key={item.Id}>
                      <td>
                        {new Date(item.StartDt).toLocaleDateString("en-GB")}
                        {item.EndDt &&
                          item.StartDt !== item.EndDt &&
                          ` - ${new Date(item.EndDt).toLocaleDateString("en-GB")}`}
                      </td>
                      <td>{item.LvType || item.LvCode}</td>
                      <td className="text-center">{item.LeaveCount}</td>
                      <td className="text-center">
                        <OverlayTrigger
                          placement="left"
                          overlay={
                            <Tooltip>
                              {item.Comments ||
                                item.reason ||
                                "No reason provided"}
                            </Tooltip>
                          }
                        >
                          <span>
                            <FontAwesomeIcon
                              icon={faComment}
                              className="text-primary"
                              style={{ cursor: "pointer" }}
                            />
                          </span>
                        </OverlayTrigger>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
};

export default LeaveAccurals;
